import React, {createRef, RefObject} from "react"

import EntitySelectorProjectsTree from "./tree"
import {
    selectParent_filter,
    selectParent_recursiveUpdateExpandState,
    selectParent_searchAndFilter
} from "../../../../gd-ui/select-parent-new/actions/filter"
import {flattenSelectableProjectIds} from "./functions/flatten-selectable-projects"
import {EntityType} from "../../../../gd/const/item"
import ProjectSession from "../../../../model2/project/project-session"
import Workspace from "../../../../model2/space/workspaces/workspace"
import {SpaceProto} from "../../../../model2/space/space-proto"
import {ItemDisplay} from "../../../../model2/item/@type"

// Same filter "Move to"'s project-target step applies (`MoveTo_Manager` with
// `ModalMoveTo_Type.TASK/DOCUMENT/MEETING`): ACL-gated by `EntityType.PROJECT`,
// no `availableProjects` subset -- nothing is being moved here, so there is no
// project (and its descendants) to exclude.
const PARENTS_FOR = [EntityType.PROJECT]

type Props = {
    parentProjectId?: ID

    onClickProject  : {(project: ProjectSession):void}
    onTab           : {():void}
}

type State = {
    search                  : string
    highlightedProjectId?   : ID
}

// const debug = "EntitySelectorProjectFinderMain"
const debug = false

export default class ProjectFinderMain extends React.Component<Props, State> {

    private treeRef: RefObject<EntitySelectorProjectsTree> = createRef()

    constructor(props: Props) {
        super(props)

        this.state = {
            search              : '',
            highlightedProjectId: undefined,
        }
    }

    componentDidMount() {
        this.updateInitialExpand()
    }

    // Restores the pre-search expand state -- mirrors
    // `ModalMoveTo_SelectFolder.updateInitialExpand`.
    updateInitialExpand = () => {
        const selectedProjectsIds = this.props.parentProjectId ? [this.props.parentProjectId] : undefined

        _.map(_.filter(gd.spaces.spacesWithProjects, (space: SpaceProto) => space.enabled), (space: SpaceProto) => {
            const project = (space as Workspace).project
            if (project) {
                project.treeIsExpandedProjectSelector = false
            }

            selectParent_recursiveUpdateExpandState(
                space,
                selectedProjectsIds,
                undefined,
                selectedProjectsIds?.length === 1
            )
        })
    }

    onSearchChange = (value: string) => {
        if (this.state.search && !value) {
            this.setState({search: '', highlightedProjectId: undefined}, this.updateInitialExpand)
        } else {
            this.setState({search: value, highlightedProjectId: undefined})
        }
    }

    onClickProject = (projectId?: ID) => {
        const project = projectId ? gd.session.projects.get(projectId) : null

        debug && console.log(debug, 'onClickProject', projectId, project)

        if (!project) return

        this.setState({highlightedProjectId: project.id})
        this.props.onClickProject(project)
    }

    onKeyDown = (event: any) => {
        switch (event.code) {
            case 'Tab':
                event.preventDefault()
                this.props.onTab()
                break

            case 'ArrowUp':
                this.moveHighlight(-1)
                break

            case 'ArrowDown':
                this.moveHighlight(1)
                break

            case 'ArrowLeft':
                event.preventDefault()
                this.collapseToParent()
                break

            case 'ArrowRight':
                event.preventDefault()
                this.expandHighlighted()
                break

            case 'Enter':
                event.preventDefault()
                event.stopPropagation()

                if (this.state.highlightedProjectId) {
                    this.onClickProject(this.state.highlightedProjectId)
                }
                break
        }
    }

    moveHighlight = (direction: 1 | -1) => {
        const ids = flattenSelectableProjectIds()
        if (_.isEmpty(ids)) {
            return
        }

        const currentIndex = this.state.highlightedProjectId ? ids.indexOf(this.state.highlightedProjectId) : -1
        const newIndex      = _.clamp(currentIndex === -1 ? 0 : currentIndex + direction, 0, ids.length - 1)

        this.setState({highlightedProjectId: ids[newIndex]})
    }

    // The parent is already expanded -- it has to be, for the current item to
    // be visible/highlighted at all -- so collapsing it always applies.
    collapseToParent = () => {
        const project = this.state.highlightedProjectId ? gd.session.projects.get(this.state.highlightedProjectId) : null
        const parent   = project?.treeParent

        if (!parent) return

        this.treeRef.current?.onToggleExpand(parent)
        this.setState({highlightedProjectId: parent.id})
    }

    expandHighlighted = () => {
        const project = this.state.highlightedProjectId ? gd.session.projects.get(this.state.highlightedProjectId) : null

        if (!project || !project.treeHasVisibleChildren || project.treeIsExpandedProjectSelector) return

        this.treeRef.current?.onToggleExpand(project)
    }

    render() {
        const matchedProjects = this.state.search
            ? selectParent_searchAndFilter(this.state.search, PARENTS_FOR, undefined)
            : gd.session.projects.treeRoots

        if (!this.state.search) {
            selectParent_filter(gd.session.projects.treeRoots, PARENTS_FOR, undefined)
        }

        const nothingFound = !_.some(matchedProjects, (project: ProjectSession) => {
            return [ItemDisplay.VISIBLE, ItemDisplay.GHOST_VISIBLE].includes(project.treeDisplay)
        })

        debug && console.log(debug, 'render', this.state, nothingFound)

        return (
            <div className='project-selector-main'>
                {nothingFound &&
                    <div className="ui5-txt-no-accent-default flex-row-center-center w-100-percent m-t-40">
                        No folders/projects found
                    </div>
                }

                {!nothingFound &&
                    <EntitySelectorProjectsTree
                        ref                     = {this.treeRef}
                        selectedProjectId       = {this.props.parentProjectId}
                        highlightedProjectId    = {this.state.highlightedProjectId}
                        onSelectProject         = {this.onClickProject}
                    />
                }
            </div>
        )
    }
}
